/**
 * The homepage's Ventures preview on the dark slab: one row per venture with
 * its dot visual, wordmark, role and narrative.
 */
import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useLocales } from "../../core/i18n";
import { SectionTitle } from "../../ui/kit";
import { Reveal, SweepWord } from "../../ui/motion";
import { CONTAINER, SECTION_PAD, TITLE_TO_CONTENT } from "../../ui/shell";
import { VentureMark, VentureVisual, hasVentureMark } from "../ventures";

// The frame is the homepage's own: the 2am hexapod fills the column, the
// other canvas is capped and centred.
const VentureDots = ({ ventureKey }) => (
	<div
		className={
			ventureKey === "2am"
				? "aspect-square overflow-hidden relative max-h-64 sm:max-h-none"
				: "aspect-square overflow-hidden relative max-w-[260px] mx-auto"
		}
	>
		<VentureVisual ventureKey={ventureKey} className="absolute inset-0" />
	</div>
);

const VentureText = ({ venture: v }) => {
	const { t } = useLocales();
	return (
		<div>
			<Link to={`/ventures#${v.key}`} className="group inline-block">
				{hasVentureMark(v.key) ? (
					<VentureMark ventureKey={v.key} dark fontSize="clamp(2rem, 4.2vw, 3.5rem)" />
				) : (
					<h3 className="font-sligoil font-bold text-pp-bg tracking-tighter group-hover:text-pp-accent transition-colors duration-300 text-pp-display-md">
						{v.name}
					</h3>
				)}
			</Link>
			<p className="font-mono text-pp-label text-pp-on-slab-muted uppercase mt-3 tracking-pp-label">
				{v.role} · {v.focus}
			</p>
			{v.status && (
				<p className="font-mono text-pp-label text-pp-accent uppercase mt-1 mb-5 tracking-pp-label">
					{v.status}
				</p>
			)}
			<p className="text-pp-on-slab text-sm sm:text-base leading-relaxed mb-6 max-w-[62ch]">
				{v.narrative}
			</p>
			<div className="flex flex-wrap items-center gap-x-8 gap-y-1">
				<Link to={`/ventures#${v.key}`} className="pp-link pp-link-dark">
					{t("homepage.readMore")}
				</Link>
				<a
					href={`https://${v.website}`}
					target="_blank"
					rel="noopener noreferrer"
					className="pp-link pp-link-dark"
				>
					→ {v.website}
				</a>
			</div>
		</div>
	);
};

/** Ventures from the locale catalog (startups.venturesData), separated by slab rules. */
const HomeVentures = () => {
	const { t } = useLocales();
	const ventures = /** @type {Array<{ key: string }>} */ (
		t("startups.venturesData", { returnObjects: true }) || []
	);
	return (
		<section className={`bg-pp-ink ${SECTION_PAD}`}>
			<div className={CONTAINER}>
				<SectionTitle description={t("homepage.venturesDescription")} dark>
					{t("homepage.venturesTitle")}
					<SweepWord>{t("homepage.venturesTitleHighlight")}</SweepWord>
				</SectionTitle>
				<div className={TITLE_TO_CONTENT}>
					{ventures.map((v, i) => (
						<Reveal key={v.key} delay={i * 80}>
							<div
								className={`pt-10 sm:pt-14 ${i < ventures.length - 1 ? "pb-10 sm:pb-14" : ""} ${i > 0 ? "border-t border-pp-on-slab-rule" : ""}`}
							>
								<div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-8 lg:gap-12 items-start">
									{/* Hidden as a whole on phones: hiding only its child
									    left an empty grid cell and a stray gap above the text. */}
									<div className="hidden sm:block">
										<VentureDots ventureKey={v.key} />
									</div>
									<VentureText venture={v} />
								</div>
							</div>
						</Reveal>
					))}
				</div>
			</div>
		</section>
	);
};

VentureDots.propTypes = { ventureKey: PropTypes.string.isRequired };
VentureText.propTypes = {
	/** One item of startups.venturesData. */
	venture: PropTypes.shape({
		key: PropTypes.string.isRequired,
		name: PropTypes.string,
		role: PropTypes.string,
		focus: PropTypes.string,
		status: PropTypes.string,
		narrative: PropTypes.string,
		website: PropTypes.string,
	}).isRequired,
};

export default HomeVentures;
